/**
 * Modelos prontos de miolo.
 *
 * Cada modelo é uma receita completa: formato, período, cores, tema, quadros do
 * dia (com rótulos próprios) e a lista de cadernos. Serve para o usuário sair do
 * zero com algo coerente e depois ajustar o que quiser.
 */
import { MATERIAS_ESCOLARES, criarCadernoPadrao } from '../layout/cadernos.js';
import { CICLO_DE_TREINO, FRASES_FITNESS, ROTINA_DE_CUIDADOS, SINAIS_DE_PAUSA, } from '../content/fitness.js';
import { aplicarEstiloVisual, aplicarTema } from '../validate.js';
import { quadrosDesligados } from '../types/planner.js';
export const ROTULO_CATEGORIA = {
    PESSOAL: 'Pessoal',
    FAMILIA: 'Família e datas',
    TRABALHO: 'Trabalho',
    NEGOCIOS: 'Negócios e gestão',
    ESTUDOS: 'Estudos e escolas',
    SAUDE: 'Saúde e bem-estar',
    BELEZA: 'Beleza e estética',
    SERVICOS: 'Serviços e ofícios',
    RELIGIOSO: 'Religioso',
    CRIATIVO: 'Criativo',
};
/** Atalho para montar um caderno já ajustado. */
function caderno(tipo, formato, paginas, extras = {}) {
    return { ...criarCadernoPadrao(tipo, formato, paginas), ...extras };
}
/**
 * Só os quadros indicados ligados, e todo o resto desligado.
 *
 * Exportada porque é ela que monta as duas sugestões de um clique no passo
 * *Quadros do dia*: a do estilo de conteúdo e a da agenda do modelo. Marcar
 * continua sendo decisão de quem usa — aqui só se prepara a lista.
 */
export function quadrosDaLista(ligados) {
    const ativos = new Set(ligados);
    return Object.fromEntries(Object.keys(quadrosDesligados()).map((quadro) => [quadro, ativos.has(quadro)]));
}
/** Tarefas clássicas de organização de casamento, do mais cedo ao dia. */
export const TAREFAS_DE_CASAMENTO = [
    'Definir data, estilo e orçamento',
    'Escolher e reservar o local',
    'Contratar fotógrafo e filmagem',
    'Definir o vestido e o traje',
    'Fechar buffet, bolo e doces',
    'Decoração, flores e iluminação',
    'Primeira versão da lista de convidados',
    'Convites, save the date e papelaria',
    'Escolher as alianças',
    'Reservar a lua de mel',
    'Degustação do menu',
    'Prova do vestido e ajustes',
    'Enviar os convites',
    'Confirmar todos os fornecedores',
    'Montar o cronograma do dia',
    'Cabelo, maquiagem e teste',
    'Pagamentos finais e gorjetas',
    'Separar algo azul, algo emprestado',
];
export const TAREFAS_ESCOLARES = [
    'Organizar o material da semana',
    'Fazer a lição de casa',
    'Estudar para a prova',
    'Ler o livro indicado',
    'Arrumar a mochila',
    'Treinar a letra',
];
/** Roteiro para testar um prompt (ou uma ferramenta de IA) sem se enganar. */
export const TAREFAS_DE_TESTE_IA = [
    'Escrever o objetivo do prompt em uma frase',
    'Definir o formato da resposta antes de testar',
    'Rodar o mesmo prompt em dois modelos e comparar',
    'Conferir números, datas e nomes à mão',
    'Anotar o ajuste que fez diferença',
    'Guardar o prompt que funcionou na biblioteca',
];
/** Tarefas de planejamento de projetos, do diagnóstico à entrega. */
export const TAREFAS_DE_PROJETO = [
    'Alinhar escopo e o que está fora dele',
    'Listar entregas e marcos',
    'Definir responsáveis',
    'Levantar riscos e planos B',
    'Combinar como o andamento será reportado',
    'Revisar a entrega antes de apresentar',
];
/** Etapas típicas do roteiro de uma viagem. */
export const TAREFAS_DE_VIAGEM = [
    'Reservar passagens e hospedagem',
    'Conferir documentos e seguro',
    'Montar o roteiro dia a dia',
    'Separar a mala',
    'Guardar comprovantes e contatos úteis',
];
export const MODELOS = [
    /* ------------------------------------------------------------------ */
    /* Diário da noiva                                                     */
    /* ------------------------------------------------------------------ */
    {
        id: 'diario-da-noiva',
        nome: 'Diário da Noiva',
        resumo: 'Um caderno para viver o noivado com calma, sem perder nenhum detalhe.',
        categoria: 'FAMILIA',
        destaques: [
            'Página por dia com tarefas do casamento, gratidão e como você se sentiu',
            'Checklist com 18 etapas do planejamento, de “definir a data” ao “dia do sim”',
            'Lista de convidados com contato, confirmação e mesa',
            'Fornecedores, orçamento do casamento e páginas para colar inspirações',
            'Paleta rosé com tipografia serifada e ornamento floral',
        ],
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'GRATIDAO', 'REVISAO_NOTURNA', 'HUMOR', 'AGUA'],
        aplicar: (base) => {
            const formato = 'A5';
            const comTema = aplicarTema({ ...base, formato }, 'vinho');
            return {
                ...comTema,
                nome: 'Diário da Noiva',
                formato,
                diasPorPagina: 1,
                semana: 'SEG_A_DOM',
                estilo: 'SEM_ESTILO',
                calendario: {
                    ...comTema.calendario,
                    modo: 'PERMANENTE',
                    paginas: 180,
                },
                cores: {
                    ...comTema.cores,
                    corPrincipal: '#B07A86',
                    esquema: 'ANALOGO',
                    intensidadeFundo: 0.55,
                    contrasteAlto: true,
                },
                ornamento: 'FLORAL',
                tipografia: { familia: 'SERIFA', escala: 1, trackingTitulos: 0.14 },
                escrita: {
                    estilo: 'LINHA',
                    alturaLinha: 6,
                    espacamento: 0,
                    comData: false,
                    traco: 'ONDULADA',
                    motivo: 'CORACAO',
                    motivoModo: 'AO_LONGO_DA_LINHA',
                    motivoACadaNLinhas: 1,
                    motivoEspacamentoMm: 20,
                    motivoTamanhoMm: 3.2,
                    motivoLado: 'ESQUERDA',
                    corMotivo: null,
                },
                /**
                 * Só os quadros que cabem na coluna lateral de A5 com um dia por página:
                 * “Falar com” (30 mm) e “Ideias” (40 mm) empurravam o fim da coluna para
                 * fora da folha e sumiam na impressão.
                 */
                /* Nenhum quadro marcado: ver o comentário do `quadrosSugeridos`. */
                quadros: quadrosDesligados(),
                rotulosQuadros: {
                    PRIORIDADES: 'Tarefas do casamento',
                    META_DO_DIA: 'Detalhe do dia',
                    GRATIDAO: 'Gratidão',
                    REVISAO_NOTURNA: 'Como me senti hoje',
                    HUMOR: 'Meu humor',
                    AGUA: 'Água',
                },
                tituloAnotacoes: 'Meu diário',
                cadernos: [
                    caderno('PLANEJAMENTO', formato, 3, {
                        titulo: 'Checklist do casamento',
                        itens: [...TAREFAS_DE_CASAMENTO],
                    }),
                    caderno('CONVIDADOS', formato, 4, { titulo: 'Lista de convidados' }),
                    caderno('CONTATOS', formato, 2, { titulo: 'Fornecedores' }),
                    caderno('FINANCAS', formato, 4, { titulo: 'Orçamento do casamento' }),
                    caderno('PAUTADO', formato, 6, { titulo: 'Ideias e inspirações', mostrarData: false }),
                    caderno('DESENHO', formato, 6, {
                        titulo: 'Moodboard',
                        paginasEmBranco: 6,
                        molduraDesenho: true,
                    }),
                    caderno('HABITOS', formato, 1, { titulo: 'Rotina de cuidados' }),
                ],
                paginasExtras: ['DADOS_PESSOAIS', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
                numerarPaginas: true,
            };
        },
    },
    /* ------------------------------------------------------------------ */
    /* Agenda escolar infantil                                             */
    /* ------------------------------------------------------------------ */
    {
        id: 'escolar-infantil',
        nome: 'Agenda Escolar Infantil',
        resumo: 'Dever de casa, recado para casa e horário das aulas, com linguagem de criança.',
        categoria: 'ESTUDOS',
        destaques: [
            'Quadros escritos para a criança: “Dever de casa”, “O que aprendi”, “Recado para casa”',
            'Horário escolar com a grade da semana e a tabela de matérias e professores',
            'Folhas de caligrafia para treinar a letra e caderno de desenho livre',
            'Página com nome, telefone dos pais e alergias — essencial na mochila',
            'Paleta coral vibrante, tipografia sem serifa e ornamento geométrico',
        ],
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'HUMOR', 'AGUA'],
        aplicar: (base) => {
            const formato = 'A5';
            const comTema = aplicarTema({ ...base, formato }, 'coral-vivo');
            return {
                ...comTema,
                nome: 'Agenda Escolar',
                formato,
                diasPorPagina: 1,
                semana: 'SEG_A_SEX',
                estilo: 'SEM_ESTILO',
                calendario: {
                    ...comTema.calendario,
                    modo: 'PERMANENTE',
                    paginas: 200,
                },
                cores: {
                    ...comTema.cores,
                    corPrincipal: '#2E7CC4',
                    esquema: 'TRIADE',
                    intensidadeFundo: 0.5,
                    contrasteAlto: true,
                },
                ornamento: 'GEOMETRICO',
                tipografia: { familia: 'SANS', escala: 1.02, trackingTitulos: 0.14 },
                escrita: {
                    estilo: 'LINHA',
                    alturaLinha: 7,
                    espacamento: 0,
                    comData: false,
                    traco: 'ZIGUE',
                    motivo: 'PATINHA',
                    motivoModo: 'AO_LONGO_DA_LINHA',
                    motivoACadaNLinhas: 1,
                    motivoEspacamentoMm: 22,
                    motivoTamanhoMm: 3.4,
                    motivoLado: 'ESQUERDA',
                    corMotivo: null,
                },
                /** “Ideias” (40 mm) não cabe na coluna lateral de A5 com um dia por página. */
                quadros: quadrosDesligados(),
                rotulosQuadros: {
                    PRIORIDADES: 'Dever de casa',
                    META_DO_DIA: 'O que aprendi hoje',
                    CONTATOS_DO_DIA: 'Recado para casa',
                    REVISAO_NOTURNA: 'Como foi meu dia',
                    HUMOR: 'Meu humor',
                    AGUA: 'Água',
                },
                tituloAnotacoes: 'Minhas anotações',
                cadernos: [
                    caderno('HORARIO', formato, 1, {
                        titulo: 'Horário das aulas',
                        materias: [...MATERIAS_ESCOLARES],
                    }),
                    caderno('PLANEJAMENTO', formato, 2, {
                        titulo: 'Minhas tarefas',
                        itens: [...TAREFAS_ESCOLARES],
                    }),
                    caderno('CALIGRAFIA', formato, 8, {
                        titulo: 'Treino de letra',
                        alturaLinhaCaligrafiaMm: 18,
                    }),
                    caderno('ESTUDOS', formato, 4, { titulo: 'Fichas de estudo' }),
                    caderno('DESENHO', formato, 6, {
                        titulo: 'Meu caderno de desenho',
                        paginasEmBranco: 6,
                        molduraDesenho: true,
                    }),
                    caderno('CONTATOS', formato, 1, { titulo: 'Telefone dos amigos' }),
                    caderno('HABITOS', formato, 1, { titulo: 'Minha rotina' }),
                ],
                paginasExtras: ['DADOS_PESSOAIS', 'MESES', 'ANIVERSARIOS', 'INDICE'],
                numerarPaginas: true,
            };
        },
    },
    /* ------------------------------------------------------------------ */
    /* Devocional                                                          */
    /* ------------------------------------------------------------------ */
    {
        id: 'devocional',
        nome: 'Agenda Devocional',
        resumo: 'Um dia por página com palavra, oração e o plano de leitura da Bíblia.',
        categoria: 'RELIGIOSO',
        destaques: [
            'Palavra do dia, oração e gratidão em cada página',
            'Plano de leitura em 365 dias, no cânon evangélico ou católico',
            'Diário de orações com pedido e resposta',
            'Paleta lavanda com ornamento de arco',
        ],
        quadrosSugeridos: ['PALAVRA_DO_DIA', 'ORACAO', 'GRATIDAO', 'REVISAO_NOTURNA', 'AGUA', 'HUMOR'],
        aplicar: (base) => {
            const formato = 'A5';
            const comTema = aplicarTema({ ...base, formato }, 'lavanda');
            return {
                ...comTema,
                nome: 'Agenda Devocional',
                formato,
                diasPorPagina: 1,
                semana: 'SEG_A_DOM',
                estilo: 'DEVOCIONAL',
                calendario: { ...comTema.calendario, modo: 'DATADO' },
                escrita: {
                    estilo: 'LINHA',
                    alturaLinha: 6,
                    espacamento: 0,
                    comData: false,
                    traco: 'PONTILHADA',
                    motivo: 'FLOR',
                    motivoModo: 'AO_LONGO_DA_LINHA',
                    motivoACadaNLinhas: 1,
                    motivoEspacamentoMm: 20,
                    motivoTamanhoMm: 3.2,
                    motivoLado: 'ESQUERDA',
                    corMotivo: null,
                },
                quadros: quadrosDesligados(),
                rotulosQuadros: {},
                tituloAnotacoes: 'Registros do dia',
                cadernos: [
                    caderno('LEITURA_BIBLIA', formato),
                    caderno('ORACOES', formato, 10),
                    caderno('PAUTADO', formato, 6, { titulo: 'Sermões e anotações', mostrarData: true }),
                ],
                paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
                numerarPaginas: true,
            };
        },
    },
    /* ------------------------------------------------------------------ */
    /* Trabalho                                                            */
    /* ------------------------------------------------------------------ */
    {
        id: 'trabalho',
        nome: 'Planner de Trabalho',
        resumo: 'Só dias úteis, com prioridades, metas e controle de gastos.',
        categoria: 'TRABALHO',
        destaques: [
            'Segunda a sexta, com prioridades e meta do dia',
            'Reuniões, falar com e revisão noturna',
            'Controle de gastos, hábitos e checklist de projetos',
            'Paleta grafite minimalista',
        ],
        quadrosSugeridos: [
            'FRASE_DO_DIA',
            'PRIORIDADES',
            'META_DO_DIA',
            'CONTATOS_DO_DIA',
            'REVISAO_NOTURNA',
            'AGUA',
            'TEMPO',
            'FINANCAS',
        ],
        aplicar: (base) => {
            const formato = 'A4';
            const comTema = aplicarTema({ ...base, formato }, 'grafite');
            return {
                ...comTema,
                nome: 'Planner de Trabalho',
                formato,
                diasPorPagina: 1,
                semana: 'SEG_A_SEX',
                estilo: 'MOTIVACIONAL',
                calendario: { ...comTema.calendario, modo: 'PERMANENTE', paginas: 220 },
                escrita: {
                    estilo: 'PONTOS',
                    alturaLinha: 7,
                    espacamento: 0,
                    comData: false,
                    traco: 'SOLIDA',
                    motivo: 'MALETA',
                    motivoModo: 'AO_LONGO_DA_LINHA',
                    motivoACadaNLinhas: 1,
                    motivoEspacamentoMm: 22,
                    motivoTamanhoMm: 3.2,
                    motivoLado: 'ESQUERDA',
                    corMotivo: null,
                },
                quadros: quadrosDesligados(),
                rotulosQuadros: {
                    PRIORIDADES: 'Prioridades do dia',
                    CONTATOS_DO_DIA: 'Quem cobrar hoje',
                    REVISAO_NOTURNA: 'Fechamento do dia',
                    FINANCAS: 'Gastos do dia',
                },
                tituloAnotacoes: 'Notas e decisões',
                cadernos: [
                    caderno('PLANEJAMENTO', formato, 4, { titulo: 'Projetos e prazos' }),
                    caderno('CONTATOS', formato, 2, { titulo: 'Contatos de trabalho' }),
                    caderno('FINANCAS', formato, 4, { titulo: 'Despesas e reembolsos' }),
                    caderno('HABITOS', formato, 2),
                    caderno('PAUTADO', formato, 8, { titulo: 'Atas de reunião', mostrarData: true }),
                ],
                paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'METAS', 'INDICE'],
                numerarPaginas: true,
            };
        },
    },
    /* ------------------------------------------------------------------ */
    /* Estudos                                                             */
    /* ------------------------------------------------------------------ */
    {
        id: 'estudos',
        nome: 'Planner de Estudos',
        resumo: 'Fichas de estudo, horário de aulas e revisão espaçada.',
        categoria: 'ESTUDOS',
        destaques: [
            'Fichas por matéria, com resumo e dúvidas',
            'Horário de aulas e tabela de professores',
            'Folhas de caligrafia e caderno pautado para resumos',
            'Paleta azul-oceano com ornamento de arco',
        ],
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'HABITOS', 'REVISAO_NOTURNA', 'AGUA'],
        aplicar: (base) => {
            const formato = 'A4';
            const comTema = aplicarTema({ ...base, formato }, 'oceano');
            return {
                ...comTema,
                nome: 'Planner de Estudos',
                formato,
                diasPorPagina: 1,
                semana: 'SEG_A_DOM',
                estilo: 'SEM_ESTILO',
                calendario: { ...comTema.calendario, modo: 'PERMANENTE', paginas: 240 },
                escrita: {
                    estilo: 'LINHA',
                    alturaLinha: 7,
                    espacamento: 0,
                    comData: false,
                    traco: 'TRACEJADA',
                    motivo: 'LIVRO',
                    motivoModo: 'AO_LONGO_DA_LINHA',
                    motivoACadaNLinhas: 1,
                    motivoEspacamentoMm: 20,
                    motivoTamanhoMm: 3.2,
                    motivoLado: 'ESQUERDA',
                    corMotivo: null,
                },
                quadros: quadrosDesligados(),
                rotulosQuadros: {
                    PRIORIDADES: 'Matérias de hoje',
                    META_DO_DIA: 'Meta de estudo',
                    HABITOS: 'Revisão espaçada',
                    REVISAO_NOTURNA: 'O que ficou pendente',
                },
                tituloAnotacoes: 'Resumo da sessão',
                cadernos: [
                    caderno('HORARIO', formato, 1, { titulo: 'Horário das aulas', materias: [...MATERIAS_ESCOLARES] }),
                    caderno('ESTUDOS', formato, 10, { titulo: 'Fichas de estudo' }),
                    caderno('PLANEJAMENTO', formato, 4, { titulo: 'Cronograma de provas' }),
                    caderno('PAUTADO', formato, 12, { titulo: 'Resumos', mostrarData: false }),
                    caderno('CALIGRAFIA', formato, 4, { titulo: 'Treino de escrita' }),
                    caderno('HABITOS', formato, 2, { titulo: 'Rotina de estudos' }),
                ],
                paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'METAS', 'INDICE'],
                numerarPaginas: true,
            };
        },
    },
];
function declararCaderno(entrada, formato) {
    if (typeof entrada === 'string')
        return caderno(entrada, formato);
    return caderno(entrada[0], formato, entrada[1], entrada[2] ?? {});
}
function receitaParaModelo(receita) {
    return {
        id: receita.id,
        nome: receita.nome,
        resumo: receita.resumo,
        categoria: receita.categoria,
        destaques: receita.destaques,
        quadrosSugeridos: receita.quadrosSugeridos,
        aplicar: (base, opcoes) => {
            const formato = receita.formato;
            const comTema = aplicarTema({ ...base, formato }, receita.tema);
            const alturaPadrao = formato === 'A4' ? 7 : formato === 'A5' ? 6 : 5;
            const escrita = {
                estilo: receita.escrita?.estilo ?? 'LINHA',
                alturaLinha: receita.escrita?.alturaLinha ?? alturaPadrao,
                espacamento: 0,
                comData: false,
                traco: receita.escrita?.traco ?? 'SOLIDA',
                motivo: receita.escrita?.motivo ?? 'NENHUM',
                motivoModo: receita.escrita?.motivoModo ?? 'AO_LONGO_DA_LINHA',
                motivoACadaNLinhas: 1,
                motivoEspacamentoMm: receita.escrita?.motivoEspacamentoMm ?? 20,
                motivoTamanhoMm: receita.escrita?.motivoTamanhoMm ?? 3.2,
                motivoLado: receita.escrita?.motivoLado ?? 'ESQUERDA',
                corMotivo: receita.escrita?.corMotivo ?? null,
            };
            const config = {
                ...comTema, nome: receita.nome,
                formato,
                diasPorPagina: receita.diasPorPagina ?? 1,
                semana: receita.semana,
                estilo: receita.estilo ?? 'SEM_ESTILO',
                calendario: {
                    ...comTema.calendario,
                    modo: receita.datado ? 'DATADO' : 'PERMANENTE',
                    paginas: receita.periodo,
                },
                cores: {
                    ...comTema.cores,
                    corPrincipal: receita.corPrincipal,
                    esquema: receita.esquema ?? 'ANALOGO',
                    corDestaque: null,
                    intensidadeFundo: receita.intensidadeFundo ?? 0.45,
                    contrasteAlto: true,
                },
                ornamento: receita.ornamento,
                papel: { ...comTema.papel, ...(receita.papel ?? {}) },
                tipografia: receita.tipografia ?? { familia: 'SANS', escala: 1, trackingTitulos: 0.14 },
                escrita,
                /*
                 * Nenhum quadro marcado: quem escolhe o que aparece na página do dia é
                 * quem usa o assistente, e a regra vale para os 25 modelos
                 * (27/09/2026). A curadoria da agenda vira sugestão de um clique no
                 * passo *Quadros do dia* — ver `quadrosSugeridos` na receita e no
                 * `ModeloMiolo`.
                 */
                quadros: quadrosDesligados(),
                rotulosQuadros: receita.rotulosQuadros ?? {},
                tituloAnotacoes: receita.tituloAnotacoes ?? '',
                cadernos: receita.cadernos.map((entrada) => declararCaderno(entrada, formato)),
                paginasExtras: receita.paginasExtras ?? ['DADOS_PESSOAIS', 'ANO', 'MESES', 'METAS', 'INDICE'],
                numerarPaginas: true,
            };
            return config;
        },
    };
}
const PADRAO_EXTRAS = ['DADOS_PESSOAIS', 'ANO', 'MESES', 'METAS', 'INDICE'];
const RECEITAS_PROFISSIONAIS = [
    /* ------------------------------------------------------------------ */
    /* Negócios e gestão                                                   */
    /* ------------------------------------------------------------------ */
    {
        id: 'empresarial',
        nome: 'Agenda Empresarial',
        resumo: 'Reuniões, projetos, orçamentos e caixa — tudo no mesmo lugar, só em dias úteis.',
        categoria: 'NEGOCIOS',
        destaques: [
            'Reuniões com pauta pronta, decisões e responsáveis',
            'Painel de projetos com responsável, prazo e situação',
            'Orçamentos para acompanhar o que foi aprovado',
            'Controle de gastos e carteira de contatos',
            'Linhas com maletas desenhadas, no estilo executivo',
        ],
        formato: 'A4',
        tema: 'grafite',
        corPrincipal: '#1F3A5F',
        esquema: 'COMPLEMENTAR',
        intensidadeFundo: 0.35,
        ornamento: 'LINHA_FINA',
        tipografia: { familia: 'MISTA', escala: 1, trackingTitulos: 0.16 },
        estilo: 'MOTIVACIONAL',
        semana: 'SEG_A_SEX',
        periodo: 260,
        escrita: { traco: 'SOLIDA', motivo: 'MALETA', motivoEspacamentoMm: 22, motivoTamanhoMm: 3.4 },
        quadrosSugeridos: ['FRASE_DO_DIA', 'PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'FINANCAS'],
        rotulosQuadros: {
            PRIORIDADES: 'Prioridades do dia',
            META_DO_DIA: 'Meta do dia',
            CONTATOS_DO_DIA: 'Quem cobrar hoje',
            REVISAO_NOTURNA: 'Fechamento do dia',
            FINANCAS: 'Faturamento e despesas',
        },
        tituloAnotacoes: 'Notas e decisões',
        cadernos: [
            ['REUNIOES', 4],
            ['PROJETOS', 4],
            ['ORCAMENTOS', 4],
            ['FINANCAS', 4, { titulo: 'Fluxo de caixa' }],
            ['CONTATOS', 3, { titulo: 'Clientes e fornecedores' }],
            ['PLANEJAMENTO', 3, { titulo: 'Plano do trimestre', itens: [...TAREFAS_DE_PROJETO] }],
            ['HABITOS', 2, { titulo: 'Rituais de gestão' }],
        ],
    },
    {
        id: 'gestor-projetos',
        nome: 'Agenda do Gestor de Projetos',
        resumo: 'Foco em entregas: riscos, marcos, atas de reunião e o que está travando.',
        categoria: 'NEGOCIOS',
        destaques: [
            'Painel de projetos com prazo e situação',
            'Ata de reunião com decisões e próximos passos',
            'Riscos, marcos e responsáveis por entrega',
            'Linhas com gráficos desenhados, no lugar de pauta comum',
            'Quadros renomeados: “tarefas críticas”, “marco do dia”, “diário de bordo”',
        ],
        formato: 'A4',
        tema: 'oceano',
        corPrincipal: '#0F6E8C',
        esquema: 'TRIADE',
        intensidadeFundo: 0.4,
        ornamento: 'GEOMETRICO',
        semana: 'SEG_A_SEX',
        periodo: 250,
        escrita: { traco: 'DUPLA', motivo: 'GRAFICO', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.4 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'HABITOS', 'TEMPO'],
        rotulosQuadros: {
            PRIORIDADES: 'Tarefas críticas',
            META_DO_DIA: 'Marco do dia',
            CONTATOS_DO_DIA: 'Quem destravar hoje',
            REVISAO_NOTURNA: 'Diário de bordo',
            HABITOS: 'Rituais de gestão',
            TEMPO: 'Horas focadas',
        },
        tituloAnotacoes: 'Riscos, decisões e combinados',
        cadernos: [
            ['REUNIOES', 6],
            ['PROJETOS', 6],
            ['PLANEJAMENTO', 4, { titulo: 'Backlog e próximos passos', itens: [...TAREFAS_DE_PROJETO] }],
            ['ORCAMENTOS', 3, { titulo: 'Propostas e contratos' }],
            ['CONTATOS', 3, { titulo: 'Time e fornecedores' }],
            ['PAUTADO', 6, { titulo: 'Notas técnicas' }],
            ['HABITOS', 2, { titulo: 'Rituais de gestão' }],
        ],
    },
    {
        id: 'financas',
        nome: 'Agenda Financeira Pessoal',
        resumo: 'Para organizar o dinheiro: entradas, saídas, dívidas e metas do mês.',
        categoria: 'PESSOAL',
        destaques: [
            'Página por dia com entradas, saídas e fechamento',
            'Controle de gastos mês a mês, com categorias',
            'Metas, dívidas e planejamento do mês',
            'Caderno de controle de gastos, com rastreador de hábitos',
            'Linhas com gráficos desenhados, para acompanhar a evolução',
        ],
        formato: 'A5',
        tema: 'floresta',
        corPrincipal: '#276749',
        esquema: 'MONOCROMATICO',
        intensidadeFundo: 0.35,
        ornamento: 'GEOMETRICO',
        semana: 'SEG_A_DOM',
        periodo: 240,
        escrita: { traco: 'DUPLA', motivo: 'GRAFICO', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        /**
         * “Hábitos” (44 mm) não cabe na coluna lateral de A5 com um dia por página.
         * O rastreador de hábitos segue no caderno “Controle de gastos”.
         */
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'FINANCAS', 'REVISAO_NOTURNA', 'GRATIDAO'],
        rotulosQuadros: {
            PRIORIDADES: 'Contas do dia',
            META_DO_DIA: 'Meta financeira',
            FINANCAS: 'Entradas e saídas',
            REVISAO_NOTURNA: 'Fechamento do dia',
            GRATIDAO: 'Conquistas do dia',
        },
        tituloAnotacoes: 'Decisões financeiras',
        cadernos: [
            ['FINANCAS', 8, { titulo: 'Entradas e saídas' }],
            ['PLANEJAMENTO', 4, { titulo: 'Metas e dívidas' }],
            ['PAUTADO', 6, { titulo: 'Planejamento do mês', mostrarData: true }],
            ['HABITOS', 2, { titulo: 'Controle de gastos' }],
            ['SENHAS', 1, { titulo: 'Acessos e senhas' }],
            ['CONTATOS', 2, { titulo: 'Bancos e contatos' }],
        ],
    },
    {
        id: 'advogado',
        nome: 'Agenda do Advogado',
        resumo: 'Prazos, audiências e casos, com ficha por cliente e registro de honorários.',
        categoria: 'SERVICOS',
        destaques: [
            'Prazos processuais com checklist',
            'Ficha de caso com cliente, andamento e retorno',
            'Reuniões com pauta e decisões',
            'Honorários, propostas e contatos de tribunais',
            'Linhas com balanças desenhadas, tipografia serifada e paleta sóbria',
        ],
        formato: 'A4',
        tema: 'grafite',
        corPrincipal: '#26436B',
        esquema: 'MONOCROMATICO',
        intensidadeFundo: 0.3,
        ornamento: 'LINHA_FINA',
        tipografia: { familia: 'SERIFA', escala: 1, trackingTitulos: 0.16 },
        semana: 'SEG_A_SEX',
        periodo: 260,
        escrita: { traco: 'SOLIDA', motivo: 'BALANCA', motivoEspacamentoMm: 24, motivoTamanhoMm: 3.4 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'FINANCAS'],
        rotulosQuadros: {
            PRIORIDADES: 'Prazos do dia',
            META_DO_DIA: 'Ato do dia',
            CONTATOS_DO_DIA: 'Clientes e tribunais',
            REVISAO_NOTURNA: 'Andamento do dia',
            FINANCAS: 'Honorários e custas',
        },
        tituloAnotacoes: 'Notas do processo',
        cadernos: [
            ['PLANEJAMENTO', 6, { titulo: 'Prazos processuais' }],
            ['PACIENTES', 6, { titulo: 'Ficha de caso' }],
            ['REUNIOES', 4, { titulo: 'Reuniões com clientes' }],
            ['ESTUDOS', 6, { titulo: 'Jurisprudência e teses' }],
            ['ORCAMENTOS', 3, { titulo: 'Honorários' }],
            ['CONTATOS', 3, { titulo: 'Clientes e tribunais' }],
            ['PAUTADO', 8, { titulo: 'Minutas e modelos' }],
        ],
    },
    /* ------------------------------------------------------------------ */
    /* Saúde e bem-estar                                                   */
    /* ------------------------------------------------------------------ */
    {
        id: 'dentista',
        nome: 'Agenda do Dentista',
        resumo: 'Consultório organizado: pacientes, orçamentos, materiais e recebimentos.',
        categoria: 'SAUDE',
        destaques: [
            'Ficha de atendimento com queixa, evolução e retorno',
            'Orçamentos com validade e situação da aprovação',
            'Estoque de materiais e controle de recebimentos',
            'Linhas com dentinhos desenhados, em turquesa suave',
            'Ficha de emergência para alergias e contatos',
        ],
        formato: 'A5',
        tema: 'menta',
        corPrincipal: '#2F9BA8',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.45,
        ornamento: 'ARCO',
        semana: 'SEG_A_SEX',
        periodo: 220,
        escrita: { motivo: 'DENTE', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'FINANCAS', 'AGUA'],
        rotulosQuadros: {
            PRIORIDADES: 'Agenda do consultório',
            META_DO_DIA: 'Meta de atendimentos',
            CONTATOS_DO_DIA: 'Ligar para pacientes',
            REVISAO_NOTURNA: 'Fechamento clínico',
            FINANCAS: 'Recebimentos',
        },
        tituloAnotacoes: 'Observações clínicas',
        cadernos: [
            ['PACIENTES', 8],
            ['ORCAMENTOS', 4, { titulo: 'Orçamentos e tratamentos' }],
            ['ESTOQUE', 2, { titulo: 'Materiais e estoque' }],
            ['CONTATOS', 3, { titulo: 'Pacientes e laboratórios' }],
            ['FINANCAS', 4, { titulo: 'Recebimentos' }],
            ['EMERGENCIA', 1],
            ['PLANEJAMENTO', 2, { titulo: 'Rotina do consultório' }],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
    },
    {
        id: 'medico',
        nome: 'Agenda do Médico',
        resumo: 'Prontuário de consultas, interconsultas, plantões e honorários.',
        categoria: 'SAUDE',
        destaques: [
            'Prontuário por consulta: queixa, evolução e retorno',
            'Ficha de emergência com alergias e plano de saúde',
            'Interconsultas, laboratórios e colegas na mão',
            'Anotações de congressos e artigos',
            'Linhas com batimentos cardíacos desenhados',
        ],
        formato: 'A5',
        tema: 'oceano',
        corPrincipal: '#1E6F9F',
        esquema: 'COMPLEMENTAR_DIVIDIDO',
        intensidadeFundo: 0.4,
        ornamento: 'LINHA_FINA',
        tipografia: { familia: 'MISTA', escala: 1, trackingTitulos: 0.15 },
        semana: 'SEG_A_DOM',
        periodo: 240,
        escrita: { motivo: 'PULSO', motivoEspacamentoMm: 22, motivoTamanhoMm: 3.4 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'FINANCAS', 'AGUA'],
        rotulosQuadros: {
            PRIORIDADES: 'Pacientes do dia',
            META_DO_DIA: 'Prioridades clínicas',
            CONTATOS_DO_DIA: 'Interconsultas e contatos',
            REVISAO_NOTURNA: 'Evoluções do dia',
            FINANCAS: 'Honorários',
        },
        tituloAnotacoes: 'Evolução e condutas',
        cadernos: [
            ['PACIENTES', 10, { titulo: 'Prontuário de consultas' }],
            ['EMERGENCIA', 2],
            ['CONTATOS', 3, { titulo: 'Colegas e laboratórios' }],
            ['ORCAMENTOS', 3, { titulo: 'Procedimentos e valores' }],
            ['PAUTADO', 6, { titulo: 'Congressos e artigos' }],
            ['FINANCAS', 4, { titulo: 'Honorários' }],
            ['ESTOQUE', 1],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
    },
    {
        id: 'nutricionista',
        nome: 'Agenda da Nutricionista',
        resumo: 'Acompanhamento por paciente, receitas e substituições na ponta do lápis.',
        categoria: 'SAUDE',
        destaques: [
            'Ficha de acompanhamento com evolução e retorno',
            'Receitas e substituições para entregar ao paciente',
            'Orçamentos de planos e pacotes',
            'Rastreador de hábitos e rotina de cuidados',
            'Linhas com folhas desenhadas, em verde suave',
        ],
        formato: 'A5',
        tema: 'floresta',
        corPrincipal: '#4C8C4A',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.45,
        ornamento: 'FLORAL',
        semana: 'SEG_A_SEX',
        periodo: 220,
        escrita: { traco: 'PONTILHADA', motivo: 'FOLHA', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'AGUA', 'FINANCAS'],
        rotulosQuadros: {
            PRIORIDADES: 'Atendimentos de hoje',
            META_DO_DIA: 'Meta do dia',
            CONTATOS_DO_DIA: 'Retornos e contatos',
            REVISAO_NOTURNA: 'Evolução dos pacientes',
            FINANCAS: 'Recebimentos',
        },
        tituloAnotacoes: 'Condutas e orientações',
        cadernos: [
            ['PACIENTES', 8, { titulo: 'Acompanhamento de pacientes' }],
            ['RECEITAS', 4, { titulo: 'Receitas e substituições' }],
            ['ORCAMENTOS', 3, { titulo: 'Planos e pacotes' }],
            ['HABITOS', 2, { titulo: 'Rotina de cuidados' }],
            ['CONTATOS', 2],
            ['PAUTADO', 4, { titulo: 'Estudos e artigos' }],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
    },
    {
        id: 'personal',
        nome: 'Agenda do Personal Trainer',
        resumo: 'Treinos, avaliações e mensalidades dos alunos, com periodização.',
        categoria: 'SAUDE',
        destaques: [
            'Ficha de treino com séries, repetições e carga',
            'Avaliação por aluno, com evolução e retorno',
            'Periodização e planejamento de ciclos',
            'Mensalidades e controle de alunos',
            'Linhas com estrelas desenhadas, em terracota vibrante',
        ],
        formato: 'A5',
        tema: 'terracota',
        corPrincipal: '#C4553A',
        esquema: 'TRIADE',
        intensidadeFundo: 0.4,
        ornamento: 'GEOMETRICO',
        tipografia: { familia: 'SANS', escala: 1.02, trackingTitulos: 0.14 },
        semana: 'SEG_A_SAB',
        periodo: 220,
        escrita: { motivo: 'ESTRELA', motivoEspacamentoMm: 18, motivoTamanhoMm: 3.2 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'REVISAO_NOTURNA', 'AGUA', 'TEMPO', 'FINANCAS'],
        rotulosQuadros: {
            PRIORIDADES: 'Treinos do dia',
            META_DO_DIA: 'Meta da semana',
            REVISAO_NOTURNA: 'Observações dos alunos',
            TEMPO: 'Clima do treino',
            FINANCAS: 'Mensalidades',
        },
        tituloAnotacoes: 'Treinos e observações',
        cadernos: [
            ['TREINO', 8],
            ['PACIENTES', 6, { titulo: 'Avaliações' }],
            ['PLANEJAMENTO', 3, { titulo: 'Periodização' }],
            ['CONTATOS', 2, { titulo: 'Alunos' }],
            ['FINANCAS', 3, { titulo: 'Mensalidades' }],
            ['HABITOS', 2],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'METAS', 'INDICE'],
    },
    /* ------------------------------------------------------------------ */
    /* Beleza e estética                                                   */
    /* ------------------------------------------------------------------ */
    {
        id: 'esmalteria',
        nome: 'Agenda da Manicure / Esmalteria',
        resumo: 'Clientes, pacotes, estoque de esmaltes e caixa do dia.',
        categoria: 'BELEZA',
        destaques: [
            'Clientes com confirmar horários e anotações do dia',
            'Pacotes, orçamentos e valores por serviço',
            'Estoque de esmaltes e materiais, com validade',
            'Caderno de inspirações de nail art',
            'Linhas onduladas com esmaltes desenhados',
        ],
        formato: 'A5',
        tema: 'vinho',
        corPrincipal: '#C2557F',
        esquema: 'COMPLEMENTAR',
        intensidadeFundo: 0.5,
        ornamento: 'FLORAL',
        tipografia: { familia: 'SERIFA', escala: 1, trackingTitulos: 0.14 },
        semana: 'SEG_A_SAB',
        periodo: 220,
        escrita: { traco: 'ONDULADA', motivo: 'ESMALTE', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'FINANCAS', 'HUMOR', 'AGUA'],
        rotulosQuadros: {
            PRIORIDADES: 'Clientes do dia',
            META_DO_DIA: 'Meta do dia',
            CONTATOS_DO_DIA: 'Confirmar horários',
            REVISAO_NOTURNA: 'Anotações do dia',
            FINANCAS: 'Caixa do dia',
            HUMOR: 'Como foi o dia',
        },
        tituloAnotacoes: 'Ideias de nail art',
        cadernos: [
            ['CONTATOS', 3, { titulo: 'Clientes' }],
            ['ORCAMENTOS', 3, { titulo: 'Pacotes e orçamentos' }],
            ['ESTOQUE', 2, { titulo: 'Esmaltes e materiais' }],
            ['FINANCAS', 4, { titulo: 'Caixa' }],
            ['DESENHO', 6, { titulo: 'Caderno de inspirações', paginasEmBranco: 6 }],
            ['HABITOS', 1, { titulo: 'Rotina do salão' }],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
    },
    {
        id: 'salao',
        nome: 'Agenda do Salão e Cabeleireiro',
        resumo: 'Agenda de clientes, produtos, caixa e portfólio de trabalhos.',
        categoria: 'BELEZA',
        destaques: [
            'Clientes do dia, com confirmação de horário',
            'Produtos e estoque, com fornecedor',
            'Caixa e controle de serviços',
            'Portfólio para colar fotos e referências',
            'Linhas onduladas com tesouras desenhadas',
        ],
        formato: 'A5',
        tema: 'lavanda',
        corPrincipal: '#7C5CBF',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.45,
        ornamento: 'FLORAL',
        tipografia: { familia: 'SERIFA', escala: 1, trackingTitulos: 0.14 },
        semana: 'SEG_A_SAB',
        periodo: 220,
        escrita: { traco: 'ONDULADA', motivo: 'TESOURA', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'FINANCAS', 'AGUA'],
        rotulosQuadros: {
            PRIORIDADES: 'Clientes de hoje',
            META_DO_DIA: 'Meta do dia',
            CONTATOS_DO_DIA: 'Confirmar horários',
            REVISAO_NOTURNA: 'Como foi o atendimento',
            FINANCAS: 'Caixa do dia',
        },
        tituloAnotacoes: 'Fórmulas e preferências',
        cadernos: [
            ['CONTATOS', 3, { titulo: 'Clientes' }],
            ['ORCAMENTOS', 3, { titulo: 'Serviços e pacotes' }],
            ['ESTOQUE', 2, { titulo: 'Produtos' }],
            ['FINANCAS', 4, { titulo: 'Caixa' }],
            ['DESENHO', 6, { titulo: 'Portfólio e referências', paginasEmBranco: 6 }],
            ['HABITOS', 1, { titulo: 'Rotina do salão' }],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
    },
    /* ------------------------------------------------------------------ */
    /* Serviços e ofícios                                                  */
    /* ------------------------------------------------------------------ */
    {
        id: 'professor',
        nome: 'Agenda do Professor',
        resumo: 'Planejamento de aulas, reuniões de pais, notas e recados.',
        categoria: 'SERVICOS',
        destaques: [
            'Plano de aulas e horário da semana',
            'Reuniões de pais e conselho, com ata',
            'Fichas de turma, notas e observações',
            'Recados para casa e registro de turma',
            'Linhas tracejadas com lápis desenhados',
        ],
        formato: 'A4',
        tema: 'menta',
        corPrincipal: '#2E8B72',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.45,
        ornamento: 'CANTO',
        tipografia: { familia: 'MISTA', escala: 1, trackingTitulos: 0.15 },
        semana: 'SEG_A_SEX',
        periodo: 250,
        escrita: { traco: 'TRACEJADA', motivo: 'LAPIS', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'HABITOS', 'AGUA'],
        rotulosQuadros: {
            PRIORIDADES: 'Planejamento das aulas',
            META_DO_DIA: 'Objetivo da aula',
            CONTATOS_DO_DIA: 'Recados para casa',
            REVISAO_NOTURNA: 'Como foi a aula',
            HABITOS: 'Rotina de correção',
        },
        tituloAnotacoes: 'Anotações de aula',
        cadernos: [
            ['HORARIO', 1, { titulo: 'Horário das aulas', materias: [...MATERIAS_ESCOLARES] }],
            ['PLANEJAMENTO', 4, { titulo: 'Plano de aulas' }],
            ['REUNIOES', 4, { titulo: 'Reuniões de pais e conselho' }],
            ['ESTUDOS', 6, { titulo: 'Fichas de turma' }],
            ['PAUTADO', 6, { titulo: 'Registro de turma', mostrarData: true }],
            ['CONTATOS', 3, { titulo: 'Turmas e coordenação' }],
            ['CALIGRAFIA', 4, { titulo: 'Atividades de treino' }],
            ['HABITOS', 1],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
    },
    {
        id: 'confeitaria',
        nome: 'Agenda da Confeiteira',
        resumo: 'Encomendas, precificação, ingredientes e receitas testadas.',
        categoria: 'SERVICOS',
        destaques: [
            'Encomendas do dia e produção',
            'Precificação de bolos, doces e kits',
            'Ingredientes em estoque, com validade',
            'Receitas e testes com modo de preparo',
            'Linhas onduladas com docinhos desenhados',
        ],
        formato: 'A5',
        tema: 'coral-vivo',
        corPrincipal: '#D2694A',
        esquema: 'TRIADE',
        intensidadeFundo: 0.5,
        ornamento: 'FLORAL',
        tipografia: { familia: 'SERIFA', escala: 1, trackingTitulos: 0.14 },
        semana: 'SEG_A_SAB',
        periodo: 220,
        escrita: { traco: 'ONDULADA', motivo: 'CUPCAKE', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'CONTATOS_DO_DIA', 'REVISAO_NOTURNA', 'FINANCAS', 'TEMPO'],
        rotulosQuadros: {
            PRIORIDADES: 'Encomendas do dia',
            META_DO_DIA: 'Produção do dia',
            CONTATOS_DO_DIA: 'Confirmar encomendas',
            REVISAO_NOTURNA: 'O que deu certo',
            FINANCAS: 'Custos e vendas',
            TEMPO: 'Clima (para a massa)',
        },
        tituloAnotacoes: 'Receitas e testes',
        cadernos: [
            ['RECEITAS', 8],
            ['ORCAMENTOS', 4, { titulo: 'Encomendas e preços' }],
            ['ESTOQUE', 2, { titulo: 'Ingredientes' }],
            ['FINANCAS', 4, { titulo: 'Custos e lucro' }],
            ['CONTATOS', 2, { titulo: 'Clientes' }],
            ['DESENHO', 6, { titulo: 'Portfólio de bolos', paginasEmBranco: 6 }],
            ['HABITOS', 1],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'ANIVERSARIOS', 'METAS', 'INDICE'],
    },
    /* ------------------------------------------------------------------ */
    /* Estudos e pessoal                                                   */
    /* ------------------------------------------------------------------ */
    {
        id: 'estudante',
        nome: 'Agenda do Estudante',
        resumo: 'Aulas, lições de casa, provas e resumos — do horário à revisão.',
        categoria: 'ESTUDOS',
        destaques: [
            'Horário das aulas e tabela de matérias e professores',
            'Lição de casa e metas de estudo em cada página',
            'Fichas de estudo por matéria, com resumo',
            'Folhas de caligrafia e caderno de resumos',
            'Linhas com livrinhos desenhados, em azul de estudos',
        ],
        formato: 'A5',
        tema: 'oceano',
        corPrincipal: '#2563A8',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.5,
        ornamento: 'GEOMETRICO',
        semana: 'SEG_A_SAB',
        periodo: 220,
        escrita: { traco: 'TRACEJADA', motivo: 'LIVRO', motivoEspacamentoMm: 20, motivoTamanhoMm: 3.2 },
        /**
         * “Dúvidas” (40 mm) e “Hábitos” (44 mm) não cabem na coluna lateral de A5
         * com um dia por página: as dúvidas ficam nas fichas de estudo.
         */
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'REVISAO_NOTURNA', 'AGUA'],
        rotulosQuadros: {
            PRIORIDADES: 'Lição de casa',
            META_DO_DIA: 'Meta de hoje',
            REVISAO_NOTURNA: 'O que estudei',
        },
        tituloAnotacoes: 'Anotações da aula',
        cadernos: [
            ['HORARIO', 1, { titulo: 'Horário das aulas', materias: [...MATERIAS_ESCOLARES] }],
            ['ESTUDOS', 8, { titulo: 'Fichas de estudo' }],
            ['PLANEJAMENTO', 3, { titulo: 'Tarefas da semana', itens: [...TAREFAS_ESCOLARES] }],
            ['PAUTADO', 8, { titulo: 'Resumos' }],
            ['CALIGRAFIA', 4, { titulo: 'Treino de letra' }],
            ['CONTATOS', 1, { titulo: 'Colegas e professores' }],
            ['HABITOS', 1, { titulo: 'Rotina de estudo' }],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'ANO', 'MESES', 'METAS', 'INDICE'],
    },
    {
        id: 'bebe',
        nome: 'Diário do Bebê',
        resumo: 'Mamadas, sono, consultas e as primeiras descobertas, dia a dia.',
        categoria: 'FAMILIA',
        destaques: [
            'Rotina de mamadas, sono e trocas no caderno de acompanhamento',
            'Consultas, vacinas e medidas anotadas',
            'Cartas e memórias para o bebê ler no futuro',
            'Ficha de emergência com alergias e contatos',
            'Linhas pontilhadas com corações desenhados',
        ],
        formato: 'A5',
        tema: 'aurora',
        corPrincipal: '#D98CA5',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.55,
        ornamento: 'CANTO',
        tipografia: { familia: 'SERIFA', escala: 1, trackingTitulos: 0.13 },
        semana: 'SEG_A_DOM',
        periodo: 200,
        escrita: { estilo: 'LINHA', alturaLinha: 7, traco: 'PONTILHADA', motivo: 'CORACAO', motivoEspacamentoMm: 20 },
        /**
         * “Mamadas, sono e trocas” (44 mm) não cabe na coluna lateral de A5 com um
         * dia por página: o registro fica no caderno “Mamadas e sono”.
         */
        quadrosSugeridos: ['PRIORIDADES', 'GRATIDAO', 'REVISAO_NOTURNA', 'HUMOR', 'AGUA'],
        rotulosQuadros: {
            PRIORIDADES: 'Rotina do bebê',
            GRATIDAO: 'Momentos do dia',
            REVISAO_NOTURNA: 'Como foi a noite',
            HUMOR: 'Humor do bebê',
            AGUA: 'Água da mamãe',
        },
        tituloAnotacoes: 'Meu diário do bebê',
        cadernos: [
            ['EMERGENCIA', 2],
            ['HABITOS', 2, { titulo: 'Mamadas e sono' }],
            ['PACIENTES', 4, { titulo: 'Consultas e vacinas' }],
            ['PAUTADO', 8, { titulo: 'Cartas para o bebê' }],
            ['DESENHO', 6, { titulo: 'Primeiros desenhos', paginasEmBranco: 6 }],
            ['CONTATOS', 2, { titulo: 'Pediatra e família' }],
        ],
        paginasExtras: ['DADOS_PESSOAIS', 'MESES', 'ANIVERSARIOS', 'INDICE'],
    },
    {
        id: 'viagem',
        nome: 'Diário de Viagem',
        resumo: 'Roteiro, gastos, contatos úteis e as memórias de cada dia.',
        categoria: 'PESSOAL',
        destaques: [
            'Roteiro com checklist do que reservar',
            'Gastos da viagem separados por dia',
            'Contatos úteis e documentos',
            'Páginas para o diário de bordo e croquis',
            'Linhas tracejadas com estrelas desenhadas',
        ],
        formato: 'A5',
        tema: 'areia',
        corPrincipal: '#C98A3C',
        esquema: 'TRIADE',
        intensidadeFundo: 0.45,
        ornamento: 'ARCO',
        tipografia: { familia: 'MISTA', escala: 1, trackingTitulos: 0.14 },
        semana: 'SEG_A_DOM',
        periodo: 180,
        escrita: { traco: 'TRACEJADA', motivo: 'ESTRELA', motivoEspacamentoMm: 22, motivoTamanhoMm: 3.4 },
        quadrosSugeridos: ['PRIORIDADES', 'META_DO_DIA', 'GRATIDAO', 'FINANCAS', 'TEMPO', 'REVISAO_NOTURNA'],
        rotulosQuadros: {
            PRIORIDADES: 'Roteiro do dia',
            META_DO_DIA: 'Experiência do dia',
            GRATIDAO: 'O que valeu a pena',
            FINANCAS: 'Gastos da viagem',
            TEMPO: 'Clima',
            REVISAO_NOTURNA: 'Hoje eu vi',
        },
        tituloAnotacoes: 'Meu diário de viagem',
        cadernos: [
            ['PLANEJAMENTO', 3, { titulo: 'Roteiro', itens: [...TAREFAS_DE_VIAGEM] }],
            ['FINANCAS', 4, { titulo: 'Gastos da viagem' }],
            ['CONTATOS', 2, { titulo: 'Contatos úteis' }],
            ['PAUTADO', 10, { titulo: 'Diário de bordo', mostrarData: true }],
            ['DESENHO', 6, { titulo: 'Croquis e lembranças', paginasEmBranco: 6 }],
            ['HABITOS', 1, { titulo: 'Rotina de viagem' }],
        ],
        paginasExtras: PADRAO_EXTRAS,
    },
];
MODELOS.push(...RECEITAS_PROFISSIONAIS.map(receitaParaModelo));
/* ------------------------------------------------------------------ */
/* Criativo                                                            */
/* ------------------------------------------------------------------ */
const RECEITAS_CRIATIVAS = [
    {
        id: 'ia-e-prompts',
        nome: 'Planner de IA e Prompts',
        resumo: 'Para quem usa IA todo dia: registrar os prompts que deram certo, testar variações e medir o ganho.',
        categoria: 'CRIATIVO',
        destaques: [
            'Caderno “Biblioteca de prompts de IA”: guia impresso + fichas para registrar seus prompts',
            'Quadros do dia renomeados: “Prompt do dia”, “Tarefas com IA” e “O que testei hoje”',
            'Checklist de testes e experimentos, com o que medir antes de adotar',
            'Painel de projetos e automações, mais espaço para ideias',
            'Linhas com estrelas desenhadas e paleta de tecnologia',
        ],
        formato: 'A5',
        tema: 'meia-noite',
        corPrincipal: '#3B5BDB',
        esquema: 'TRIADE',
        intensidadeFundo: 0.4,
        ornamento: 'GEOMETRICO',
        tipografia: { familia: 'MISTA', escala: 1, trackingTitulos: 0.15 },
        estilo: 'MOTIVACIONAL',
        semana: 'SEG_A_SEX',
        periodo: 220,
        escrita: { motivo: 'ESTRELA', motivoEspacamentoMm: 22, motivoTamanhoMm: 3.4 },
        /**
         * “Ideias” (40 mm) e “Hábitos” (44 mm) não cabem na coluna lateral de A5 com
         * um dia por página: os dois seguem nos cadernos de prompts e de rotina.
         */
        quadrosSugeridos: ['META_DO_DIA', 'PRIORIDADES', 'REVISAO_NOTURNA', 'GRATIDAO'],
        rotulosQuadros: {
            META_DO_DIA: 'Prompt do dia',
            PRIORIDADES: 'Tarefas com IA',
            REVISAO_NOTURNA: 'O que testei hoje',
            GRATIDAO: 'Descobertas do dia',
        },
        tituloAnotacoes: 'Prompts e anotações',
        cadernos: [
            ['PROMPTS', 6, { titulo: 'Biblioteca de prompts de IA' }],
            ['PLANEJAMENTO', 4, {
                    titulo: 'Testes e experimentos',
                    itens: [...TAREFAS_DE_TESTE_IA],
                }],
            ['PROJETOS', 3, { titulo: 'Projetos e automações' }],
            ['ESTUDOS', 4, { titulo: 'Notas de estudo de IA' }],
            ['PAUTADO', 6, { titulo: 'Ideias de automação' }],
            ['HABITOS', 2, { titulo: 'Rotina de testes' }],
        ],
    },
];
MODELOS.push(...RECEITAS_CRIATIVAS.map(receitaParaModelo));
/* ------------------------------------------------------------------ */
/* Encadernação: wire-o e capa dura                                    */
/* ------------------------------------------------------------------ */
const RECEITAS_ENCADERNADAS = [
    {
        id: 'wire-o-escritorio',
        nome: 'Agenda Wire-o de Escritório',
        resumo: 'A4 com wire-o de 3:1 na prévia, margem de 20 mm e guia de furo impressa: pronta para encadernar na papelaria ou em casa.',
        categoria: 'TRABALHO',
        destaques: [
            'Wire-o desenhado na prévia, com a margem de 20 mm que o furo exige',
            'Guia de furo impressa na margem, para a encadernadora alinhar o furo',
            'Reuniões com pauta, projetos com responsável e prazo, orçamentos e contatos',
            'A4 em pé, de segunda a sexta, com linhas de pauta limpas',
        ],
        formato: 'A4',
        tema: 'grafite',
        corPrincipal: '#2F4A6D',
        esquema: 'COMPLEMENTAR',
        intensidadeFundo: 0.35,
        ornamento: 'LINHA_FINA',
        tipografia: { familia: 'SANS', escala: 1, trackingTitulos: 0.14 },
        estilo: 'MOTIVACIONAL',
        semana: 'SEG_A_SEX',
        periodo: 260,
        papel: {
            encadernacao: 'WIRE_O',
            guiaDeFuro: true,
            margemLombadaMm: 20,
            ladoLombada: 'ESQUERDA',
        },
        quadrosSugeridos: ['META_DO_DIA', 'PRIORIDADES', 'REVISAO_NOTURNA'],
        cadernos: [
            ['REUNIOES', 4],
            ['PROJETOS', 3],
            ['ORCAMENTOS', 3],
            ['CONTATOS', 4],
            ['FINANCAS', 4],
        ],
        paginasExtras: PADRAO_EXTRAS,
    },
    {
        id: 'capa-dura-presente',
        nome: 'Agenda Capa Dura para Presente',
        resumo: 'A5 com a lombada de capa dura desenhada na prévia, margem de 20 mm e o essencial do dia a dia — feita para dar de presente com nome.',
        categoria: 'PESSOAL',
        destaques: [
            'Lombada de capa dura desenhada na prévia (a arte da capa fica com a gráfica)',
            'Margem de encadernação de 20 mm, como pede a lombada larga',
            'Página de dados de quem recebe, para virar presente com nome',
            'Hábitos, planejamento, contatos e folhas livres — sem excesso',
            'Paleta vinho com tipografia serifada',
        ],
        formato: 'A5',
        tema: 'vinho',
        corPrincipal: '#7B3B4A',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.4,
        ornamento: 'LINHA_FINA',
        tipografia: { familia: 'SERIFA', escala: 1, trackingTitulos: 0.16 },
        estilo: 'MOTIVACIONAL',
        semana: 'SEG_A_DOM',
        periodo: 180,
        papel: { encadernacao: 'CAPA_DURA', margemLombadaMm: 20, ladoLombada: 'ESPELHADA' },
        quadrosSugeridos: ['META_DO_DIA', 'PRIORIDADES', 'GRATIDAO'],
        cadernos: [
            ['PLANEJAMENTO', 3, { titulo: 'Meu plano do mês' }],
            ['HABITOS', 2, { titulo: 'Hábitos do trimestre' }],
            ['CONTATOS', 3],
            ['PAUTADO', 6, { titulo: 'Anotações livres' }],
        ],
        paginasExtras: PADRAO_EXTRAS,
    },
];
MODELOS.push(...RECEITAS_ENCADERNADAS.map(receitaParaModelo));
/* ------------------------------------------------------------------ */
/* Fitness                                                             */
/* ------------------------------------------------------------------ */
const RECEITAS_FITNESS = [
    {
        id: 'agenda-fitness',
        nome: 'Agenda Fitness',
        resumo: 'Treino, rotina e evolução no mesmo lugar: ficha de treino, ciclo, água, sono e as frases que seguram a constância.',
        categoria: 'SAUDE',
        destaques: [
            'Ficha de treino para exercício, séries, repetições e carga',
            'Ciclo de treino em checklist: da montagem da ficha à revisão de 12 semanas',
            'Rastreador de hábitos para água, sono, passos e alongamento',
            'Quadros do dia renomeados: foco do treino, energia e como o corpo respondeu',
            'Frases de constância impressas, para os dias de preguiça',
            'Suplementos, avaliação física e refeições nos cadernos',
        ],
        formato: 'A4',
        tema: 'floresta',
        corPrincipal: '#2F6B4F',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.35,
        ornamento: 'GEOMETRICO',
        tipografia: { familia: 'SANS', escala: 1, trackingTitulos: 0.15 },
        estilo: 'MOTIVACIONAL',
        semana: 'SEG_A_DOM',
        periodo: 260,
        papel: { encadernacao: 'ESPIRAL', margemLombadaMm: 18, ladoLombada: 'ESQUERDA' },
        quadrosSugeridos: ['META_DO_DIA', 'PRIORIDADES', 'AGUA', 'REVISAO_NOTURNA'],
        rotulosQuadros: {
            META_DO_DIA: 'Foco do treino',
            PRIORIDADES: 'Treino e tarefas do dia',
            AGUA: 'Água do dia',
            REVISAO_NOTURNA: 'Como o corpo respondeu',
        },
        tituloAnotacoes: 'Treino e anotações do dia',
        cadernos: [
            ['TREINO', 6, { titulo: 'Ficha de treino' }],
            ['PLANEJAMENTO', 2, { titulo: 'Ciclo de treino', itens: [...CICLO_DE_TREINO] }],
            ['HABITOS', 2, { titulo: 'Rotina: água, sono e passos' }],
            [
                'PLANEJAMENTO',
                2,
                { titulo: 'Frases para os dias difíceis', itens: [...FRASES_FITNESS] },
            ],
            [
                'PLANEJAMENTO',
                2,
                { titulo: 'Sinais de que é hora de pausar', itens: [...SINAIS_DE_PAUSA] },
            ],
            ['PACIENTES', 3, { titulo: 'Avaliação física' }],
            ['RECEITAS', 3, { titulo: 'Refeições e preparos' }],
            ['ESTOQUE', 2, { titulo: 'Suplementos' }],
        ],
        paginasExtras: PADRAO_EXTRAS,
    },
    {
        id: 'diario-de-treino',
        nome: 'Diário de Treino',
        resumo: 'Um caderno por ciclo: o que treinei, com que carga, como dormi e o que melhorou — em A5, para levar na mochila.',
        categoria: 'SAUDE',
        destaques: [
            'Cabe na mochila: A5 com espiral e margem de 18 mm',
            'Ficha de treino com carga, repetições e observação',
            'Rotina de cuidados fora do treino em checklist',
            'Avaliação física a cada ciclo, com peso e medidas',
            'Refeições e preparos para quem cozinha no domingo',
        ],
        formato: 'A5',
        tema: 'menta',
        corPrincipal: '#2E7D74',
        esquema: 'ANALOGO',
        intensidadeFundo: 0.35,
        ornamento: 'LINHA_FINA',
        tipografia: { familia: 'MISTA', escala: 1, trackingTitulos: 0.14 },
        estilo: 'MOTIVACIONAL',
        semana: 'SEG_A_SAB',
        periodo: 180,
        papel: { encadernacao: 'ESPIRAL', margemLombadaMm: 18, ladoLombada: 'ESQUERDA' },
        quadrosSugeridos: ['META_DO_DIA', 'HABITOS', 'AGUA'],
        rotulosQuadros: {
            META_DO_DIA: 'Treino do dia',
            HABITOS: 'Rotina: sono e passos',
            AGUA: 'Água',
        },
        cadernos: [
            ['TREINO', 5, { titulo: 'Ficha de treino' }],
            [
                'PLANEJAMENTO',
                2,
                { titulo: 'Rotina de cuidados', itens: [...ROTINA_DE_CUIDADOS] },
            ],
            ['HABITOS', 2, { titulo: 'Rotina: água, sono e passos' }],
            ['PACIENTES', 2, { titulo: 'Avaliação física' }],
            ['RECEITAS', 2, { titulo: 'Refeições e preparos' }],
        ],
        paginasExtras: PADRAO_EXTRAS,
    },
];
MODELOS.push(...RECEITAS_FITNESS.map(receitaParaModelo));
/*
 * Fecha a lista: todo modelo responde ao estilo visual, inclusive os escritos à
 * mão aqui em cima, que não saem de receita.
 *
 * O envoltório fica no fim do arquivo, quando todos já foram registrados — o
 * teste `todo modelo pronto cabe na folha nos três estilos` reprova qualquer
 * modelo novo que entre na lista sem passar por aqui.
 */
for (const [indice, modelo] of MODELOS.entries()) {
    const aplicarOriginal = modelo.aplicar;
    MODELOS[indice] = {
        ...modelo,
        aplicar: (base, opcoes) => {
            const config = aplicarOriginal(base, opcoes);
            return opcoes?.estiloVisual ? aplicarEstiloVisual(config, opcoes.estiloVisual) : config;
        },
    };
}
export function obterModelo(id) {
    return MODELOS.find((modelo) => modelo.id === id);
}
/** Aplica um modelo; devolve a base intacta se o id não existir. */
export function aplicarModelo(base, id, opcoes) {
    const modelo = obterModelo(id);
    return modelo ? modelo.aplicar(base, opcoes) : base;
}
/** Modelos agrupados por categoria, na ordem em que o assistente mostra. */
export function modelosPorCategoria() {
    const ordem = [
        'PESSOAL',
        'FAMILIA',
        'TRABALHO',
        'NEGOCIOS',
        'ESTUDOS',
        'SAUDE',
        'BELEZA',
        'SERVICOS',
        'RELIGIOSO',
        'CRIATIVO',
    ];
    return ordem
        .map((categoria) => ({
        categoria,
        modelos: MODELOS.filter((modelo) => modelo.categoria === categoria),
    }))
        .filter((entrada) => entrada.modelos.length > 0);
}
//# sourceMappingURL=modelos.js.map